/* eslint-disable no-negated-condition */
import React from 'react';
import PropTypes from 'prop-types';
import {OtpPopup, agentDeveloperUtils} from 'wobi-web-common';
import {injectIntl} from 'react-intl';
import {getOffersFields, setOffersFields} from '../utils/persistOfferDetails';
import {getToken, validateSecret} from '../utils/apiHandlers';
import {fireGtmEvent, fireGtmEventWithUserId} from '../utils/marketing.utils';

const OtpContainer = ({
  open,
  handleClose,
  handleVerified,
  handleBlocked,
  showNID,
  phone,
  fullname,
  values,
  otpMethods,
  intl,
}) => {
  const [otpTokenReceived, setOtpTokenReceived] = React.useState(false);
  const [otpTokenLoading, setOtpTokenLoading] = React.useState(false);
  const [verifyLoading, setVerifyLoading] = React.useState(false);
  const [commonError, setCommonError] = React.useState('');
  const [otpToken, setOtpToken] = React.useState(false);
  const [sentOtpMethod, setSentOtpMethod] = React.useState('');

  const retryCount = React.useRef(0);
  const messages = (id) => intl.formatMessage({id});
  const handlePostOtpDetails = details => {
    // make request here
    fireGtmEvent('travelSendOtp');
    fireGtmEvent('travelExperiment', {
      value: getOffersFields('experimentVariant') || '0',
    });
    setSentOtpMethod(details?.otpMethod);
    if (details?.otpMethod === 'WhatsApp') {
      window.rollbar.info(`SendOtp ${details.otpMethod} request was fired`);
    }
    setOtpTokenLoading(true);
    const offerDetails = {
      firstname: details.fullname.split(' ')[0],
      lastname: details.fullname.split(' ')[1],
      phone: details.phoneNumber,
    };

    details.webOTP = true;
    details.agentDeveloperName = agentDeveloperUtils.getAgentDeveloperName;

    getToken({...details,
      retryCount: retryCount.current})
      .then(result => {
        values.phone = phone !== details.phoneNumber ? details.phoneNumber : '';
        values.fullname = fullname !== details.fullname ? details.fullname : '';
        setOffersFields(offerDetails);

        if (result.success) {
          setOtpToken(result.data);
          setOffersFields({
            otpToken: result.data,
          });
          setOtpTokenLoading(false);
          setOtpTokenReceived(true);
        } else if (!result.success && result.message === 'blocked user') {
          handleClose();
          handleBlocked();
          setOtpTokenLoading(false);
        }
        return null;
      })
      .catch(() => {
        setOtpTokenLoading(false);
        setCommonError(messages('otp_container.error_something_wrong_on_otp_request'));
      });
  };

  const handleVerify = data => {
    const request = Object.assign({}, {secret: data.code,
      token: otpToken});
    setVerifyLoading(true);
    validateSecret(request)
      .then(result => {
        setVerifyLoading(false);
        if (result.success) {
          fireGtmEventWithUserId(getOffersFields('phone'), 'travelConfirmOtp');
          handleClose();
          handleVerified();
        } else if (!result.success && result.message === 'wrongOtp') {
          setCommonError(messages('otp_container.error_wrong_otp'));
        } else if (!result.success && result.message === 'maxAttempts') {
          handleResend();
          setCommonError(messages('otp_container.error_max_attempts'));
          handleResend(sentOtpMethod);
        } else {
          fireGtmEvent('travelOtpInvalid');
        }
      })
      .catch(() => {
        setVerifyLoading(false);
        setCommonError('Something wrong on details request');
      });
  };

  const handleResend = (otpMethod) => {
    setSentOtpMethod(otpMethod);
    retryCount.current++;
    fireGtmEvent('travelSendAgainPassword');
    window.rollbar.warning('SendAgainPassword request was fired');
    if (otpMethod === 'WhatsApp') {
      window.rollbar.warning(`SendAgainPassword ${otpMethod} request was fired`);
    }
    getToken({
      agentDeveloperName: agentDeveloperUtils.getAgentDeveloperName,
      fullname: `${getOffersFields('firstname')} ${getOffersFields(
        'lastname',
      )}`,
      otpMethod,
      phoneNumber: getOffersFields('phone'),
      retryCount: retryCount.current,
      webOTP: true,
    })
      .then(result => {
        if (result.success) {
          setOtpToken(result.data);
          setOffersFields({token: result.data});
        }
      })
      .catch(() => {
        setCommonError('Something wrong on details request');
      });
  };

  const handleBack = () => {
    setOtpTokenReceived(false);
  };

  return (
    <OtpPopup
      open={open}
      tokenReceived={otpTokenReceived}
      commonErrorMessage={commonError}
      clearError={() => setCommonError('')}
      onClose={handleClose}
      handlePostOtpDetails={handlePostOtpDetails}
      handleVerify={handleVerify}
      handleResend={handleResend}
      handleBack={handleBack}
      tokenLoading={otpTokenLoading}
      verifyLoading={verifyLoading}
      showNID={showNID}
      data={{phone,
        fullname}}
      otpMethods={otpMethods}
      showWhatsappButton
    />
  );
};

OtpContainer.propTypes = {
  handleBlocked: PropTypes.func,
  handleClose: PropTypes.func,
  handleVerified: PropTypes.func,
  intl: PropTypes.object,
  open: PropTypes.bool,
  otpMethods: PropTypes.array,
  showNID: PropTypes.bool,
  values: PropTypes.object,
  phone: PropTypes.string,
  fullname: PropTypes.string,
};

OtpContainer.defaultProps = {
  showNID: false,
};

export default injectIntl(OtpContainer);